import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Chip from '@mui/material/Chip';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import { Table } from '../../components/Table';
import { TableRef } from '../../components/Table/Table';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { portfolioOrderStatusToInfo } from '../../constants/tables/portfolioOrder';
import { fetchPortfolioOrdersByOrderId } from '../../redux/modules/portfolioOrder/portfolioOrder.actions';
import {
  portfolioOrderSelector,
  viewDetailsByOrderIdSelector,
} from '../../redux/modules/portfolioOrder/portfolioOrder.selectors';
import { PortfolioOrderStatus } from '../../types/portfolioOrder';

interface PortfolioOrderTabProps {
  orderId: string;
}

export function PortfolioOrderTab({ orderId }: PortfolioOrderTabProps) {
  const tableRef = useRef<TableRef>(null);
  const [columns] = useState<GridColDef[]>([
    {
      field: 'id',
      headerName: 'Id',
      width: 100,
    },
    {
      field: 'orderId',
      headerName: 'Order Id',
      type: 'string',
      width: 350,
    },
    {
      field: 'userPortfolioRequest.id',
      headerName: 'Request Id',
      valueGetter: (params) => params.row?.userPortfolioRequest?.id,
      width: 120,
    },
    {
      field: 'status.name',
      headerName: 'Status',
      type: 'singleSelect',
      valueOptions: Object.values(PortfolioOrderStatus),
      minWidth: 200,
      flex: 1,
      valueGetter: (params) => params.row?.status?.name,
      renderCell: (params: GridRenderCellParams) => {
        const info = portfolioOrderStatusToInfo[params.row?.status?.name as PortfolioOrderStatus] || {};
        const Icon = info?.icon;

        if (!Icon) {
          return params.row?.status?.name;
        }

        return <Chip variant="outlined" color={info?.color} icon={<Icon />} label={params.row?.status?.name} />;
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      type: 'date',
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '';
        }

        return params.formattedValue;
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      type: 'date',
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '';
        }

        return params.formattedValue;
      },
    },
  ]);
  const { totalPortfolioOrders, portfolioOrders, fetchPortfolioOrdersStatus } =
    useSelector(viewDetailsByOrderIdSelector);
  const { executeStatus, importExecutionFileStatus } = useSelector(portfolioOrderSelector);
  const loading =
    fetchPortfolioOrdersStatus === fetchStatuses.pending ||
    importExecutionFileStatus === fetchStatuses.pending ||
    executeStatus === fetchStatuses.pending;

  useEffect(() => {
    if (importExecutionFileStatus === fetchStatuses.success || executeStatus === fetchStatuses.success) {
      tableRef.current?.reload();
    }
  }, [importExecutionFileStatus, executeStatus]);

  return (
    <Table
      ref={tableRef}
      fetchItems={fetchPortfolioOrdersByOrderId}
      rows={portfolioOrders}
      columns={columns}
      loading={loading}
      rowCount={totalPortfolioOrders}
      pageSizeOptions={[50, 100, 300]}
      initialPageSize={50}
      fetchItemsCustomParams={{ orderId }}
      toolbarExportProps={{
        csvOptions: { fileName: `portfolio-orders-${orderId}` },
        excelOptions: { fileName: `portfolio-orders-${orderId}` },
      }}
    />
  );
}
